import { isDateExpiring } from '@/lib/utils/dates'
import dayjs from '@/lib/utils/dayjs'

import {
  DeliveryContact,
  ReserveOrder,
  CartItem,
  Lot,
  Department,
  InventoryThreshold,
  OrderTemplate,
  InventoryLog,
  Address,
  ApiReserveOrderItem,
  ApiUnitCustomer,
  ApiOrderTemplate,
  ApiInventoryThreshold,
  ApiInventoryLog,
  ApiErrorItem,
  ApiErrorCommonItem,
  ApiError,
  GeneralApiErrorItem,
} from '@/types'

const recurrenceTimeFrameLabelMap = {
  day: {
    single: 'Day',
    plural: 'Days',
  },
  week: {
    single: 'Week',
    plural: 'Weeks',
  },
  month: {
    single: 'Month',
    plural: 'Months',
  },
}

export const transformDeliveryContact = (el: ApiUnitCustomer): DeliveryContact => {
  const userPhone = el.contactAddress?.phone ? el.contactAddress.phone : el.phone
  const phoneExtension = el.contactAddress?.phoneExtension
    ? `, ${el.contactAddress.phoneExtension}`
    : ''
  return {
    title: el.name,
    value: el.uid,
    email: el.uid,
    id: el.sageX3Id,
    rawPhone: el.phone || '',
    currency: el.currency,
    additionalText: [
      userPhone ? `<p>${userPhone}${phoneExtension}</p>` : '',
      el.uid ? `<p>${el.uid}</p>` : '',
      el.contactAddress?.buildingRoomNumber
        ? `<p>Building / Room #: ${el.contactAddress.buildingRoomNumber}</p>`
        : '',
    ]
      .filter((el) => el)
      .join(''),
  }
}

export const transformReserveOrder = (
  reserve: ApiReserveOrderItem,
  cartProducts: CartItem[],
): ReserveOrder => {
  const reservedOrderLine = `${reserve.reserveId}:${reserve.entryNumber}`
  const lots: Lot[] = reserve.allocationList.map((lotData) => {
    const cartProduct = cartProducts.find(
      (cartProduct) =>
        cartProduct.reservedOrderLine === reservedOrderLine &&
        cartProduct.inventory?.some(
          (cartInventory) => cartInventory.objectID === lotData.inventoryId,
        ),
    )
    return {
      objectID: lotData.inventoryId,
      lotId: lotData.lot,
      quantity: lotData.quantity,
      rollQuantityUnit: lotData.rollQuantityUnit || '',
      isSalesToStockMainPriceParam: !!(
        lotData.rollQuantityUnit &&
        !['ea', 'vil', 'vial', 'vials'].includes(lotData.rollQuantityUnit.toLowerCase())
      ),
      cartProduct,
      isAddedToCart: !!cartProduct,
      isAddToCartEnabled: !!lotData.inventoryId && !!lotData.quantity,
      reservedOrderLine,
    }
  })
  return {
    reservedOrderLine,
    reserveId: reserve.reserveId,
    entryNumber: reserve.entryNumber,
    productName: reserve.product?.name,
    productCode: reserve.product?.code,
    productPdpSlug: reserve.product?.pgpSlug,
    quantity: reserve.quantity,
    isAddedToCart: lots.some((lot) => lot.isAddedToCart),
    isAddToCartEnabled: lots.some((lot) => lot.isAddToCartEnabled),
    lots,
    endDate: {
      value: reserve.endDate,
      isExpiring: reserve.endDate ? isDateExpiring(reserve.endDate, 7) : false,
    },
    contact: reserve.contact,
  }
}

export const transformOrderTemplate = (orderTemplate: ApiOrderTemplate): OrderTemplate => {
  const b2bUnitCode = orderTemplate.b2bUnit
  const b2bUnitName = orderTemplate.b2bUnitName || orderTemplate.b2bUnit
  const b2bUnit = {
    code: b2bUnitCode,
    name: b2bUnitName,
  }

  return {
    id: orderTemplate.id,
    active: orderTemplate.active,
    b2bUnit,
    billingB2bUnit: b2bUnit,
    shippingB2bUnit: b2bUnit,
    department: orderTemplate.department,
    billTo: orderTemplate.billTo,
    billToAddressString: orderTemplate.billToAddress,
    billingContact: orderTemplate.billingContact,
    billingEmails: orderTemplate.billingEmails || [],
    contact: orderTemplate.contact,
    contactName: orderTemplate.contactName || '',
    contactEmail: orderTemplate.contactEmail || '',
    currency: orderTemplate.currency,
    name: orderTemplate.name || '',
    expirationDate: orderTemplate.expirationDate
      ? dayjs.utc(orderTemplate.expirationDate).format('YYYY-MM-DDTHH:mm:ss+00:00')
      : '',
    notes: orderTemplate.notes || '',
    poNumber: orderTemplate.poNumber || '',
    poAllowedAmount: orderTemplate.poAllowedAmount || 0,
    remainingAmount: orderTemplate.remainingAmount || 0,
    shipTo: orderTemplate.shipTo,
    shipToAddressString: orderTemplate.shipToAddress,
    shippingContact: orderTemplate.shippingContact,
    shippingEmails: orderTemplate.shippingEmails || [],
  }
}

export const transformDepartment = (department: Department): Department => ({
  id: department.id,
  businessUnit: department.businessUnit,
  name: department.name,
})

export const getRecurrenceTimeFrameLabel = (
  recurrenceTimeFrame: keyof typeof recurrenceTimeFrameLabelMap,
  recurrenceNumber: number,
) => {
  return recurrenceNumber > 1
    ? recurrenceTimeFrameLabelMap[recurrenceTimeFrame].plural
    : recurrenceTimeFrameLabelMap[recurrenceTimeFrame].single
}

export const transformInventoryThreshold = (
  threshold: ApiInventoryThreshold,
): InventoryThreshold => {
  const type = threshold.type || 'ondemand'
  const isTypeSubscription = type === 'subscription'
  return {
    name: threshold.name,
    id: threshold.id,
    type,
    isTypeQuantity: type === 'qty',
    isTypeSubscription,
    isTypeOndemand: type === 'ondemand',
    businessUnit: threshold.department.businessUnit,
    department: transformDepartment(threshold.department),
    sku: threshold.sku || '',
    skuName: threshold.skuName || '',
    lab: threshold.lab,
    orderTemplate: transformOrderTemplate(threshold.orderTemplate),
    startDate: threshold.startDate || '',
    lastExecDate: threshold.lastExecDate || '',
    nextExecDate: threshold.nextExecDate || '',
    recurrenceNumber: threshold.recurrenceNumber,
    recurrenceTimeFrame: threshold.recurrenceTimeFrame,
    recurrenceTimeFrameLabel:
      isTypeSubscription && threshold.recurrenceTimeFrame && threshold.recurrenceNumber
        ? getRecurrenceTimeFrameLabel(threshold.recurrenceTimeFrame, threshold.recurrenceNumber)
        : '',
    minimumQuantity: threshold.minimumQuantity,
    maximumQuantity: threshold.maximumQuantity,
    maxOrderQty: threshold.maxOrderQty,
    notes: threshold.notes || '',
    active: threshold.active,
    priceParameters: threshold.priceParameters,
  }
}

export const transformInventoryLog = (log: ApiInventoryLog): InventoryLog => ({
  id: log.id,
  logDate: log.logDate || 0,
  sku: log.sku || '',
  productDescription: log.productDescription || '',
  quantity: log.quantity || 0,
  originalInventory: log.originalInventory || 0,
  currentInventory: log.currentInventory || 0,
  b2bUnit: log.department?.businessUnit || '',
  departmentName: log.department?.name || '',
  po: log.po || '',
  deliveryAddress: log.deliveryAddress || '',
  order: log.order || '',
  orderStatus: log.orderStatus || '',
  orderQty: log.orderQty || 0,
})

export const transformApiFormErrors = (error: unknown): (ApiErrorItem | ApiErrorCommonItem)[] => {
  const transformedError = error as unknown as ApiError
  const errorsData = transformedError.response.data
  if (!errorsData) return [{ type: 'ValidationError', message: 'Something went wrong' }]
  if (typeof errorsData === 'string') {
    return [{ type: 'ValidationError', message: errorsData }]
  }
  if (typeof errorsData === 'object' && !Array.isArray(errorsData)) {
    return [
      {
        type: errorsData.error || 'ValidationError',
        message: errorsData.message || 'Something went wrong',
      },
    ]
  }
  const errors: Array<ApiErrorItem | ApiErrorCommonItem | GeneralApiErrorItem> = Array.isArray(
    errorsData,
  )
    ? errorsData
    : [errorsData]
  const fieldErrors: ApiErrorItem[] = []
  let commonErrors: ApiErrorCommonItem[] = []

  const splitErrorsBySubject = (
    errors: Array<ApiErrorItem | ApiErrorCommonItem | GeneralApiErrorItem>,
  ): ApiErrorItem[] => {
    const splitedErrors: ApiErrorItem[] = []
    if (errors && Array.isArray(errors)) {
      errors.forEach((error) => {
        if ('subject' in error) {
          const subjects = error.subject.split(',')
          if (subjects.length) {
            subjects.forEach((subject: string) => {
              splitedErrors.push({ ...error, subject })
            })
          }
        }
      })
    }
    return splitedErrors
  }
  const splitedErrors = splitErrorsBySubject(errors)

  const fieldErrorsMap: Record<string, Set<string>> = {}
  const uniqueCommonErrors = new Set<string>()
  splitedErrors.forEach((error) => {
    const fieldName = error.subject
    const message = error.message

    if (!fieldName) {
      uniqueCommonErrors.add(message)
      return
    }

    const isFieldInMap = fieldName in fieldErrorsMap
    if (!isFieldInMap) fieldErrorsMap[fieldName] = new Set()
    fieldErrorsMap[fieldName].add(message)
  })

  Object.entries(fieldErrorsMap).forEach(([field, messages]) => {
    fieldErrors.push({
      type: 'ValidationError',
      subject: field,
      message: Array.from(messages as Set<string>).join('; '),
    })
  })

  commonErrors = Array.from(uniqueCommonErrors).map((message) => ({
    type: 'ValidationError',
    message,
  }))

  return [...fieldErrors, ...commonErrors]
}
export const transformApiErrorsToString = (error: unknown): string => {
  return transformApiFormErrors(error)
    .map((el) => el.message)
    .join('; ')
}

export const addressToString = ({
  companyName,
  line1,
  line2,
  line3,
  town = '',
  region,
  postalCode,
  country,
}: Address): string => {
  return [
    companyName,
    line1,
    line2,
    line3,
    [town.trim() ? `${town.trim()},` : '', region?.name, postalCode]
      .filter((el) => el)
      .map((el) => el.trim())
      .filter((el) => el)
      .join(' '),
    country?.name,
  ]
    .filter((el) => el)
    .map((el) => el.trim())
    .filter((el) => el)
    .join(', ')
}
